/**
 * "Frontend structure" (docs section 8): the layers of `frontend/src` from top to bottom.
 * The entry files (a Timeline with arrows), the feature folders with the path of each
 * page, the three shared folders (components, the maps of record in `lib`, the API barrel
 * with the generated client), and the tests that guard them. The arrows say what a layer
 * does with the layer under it. On a phone every layer is one column (narrow query).
 */

import { ArrowDown, FlaskConical, Folder, Icon } from "@norican/icons";
import { Badge, Card, Timeline, type TimelineItem, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

export const FRONTEND_STRUCTURE_TITLE = "Frontend structure";

/** What the drawing shows, for a screen reader. */
export const ENTRY_SUMMARY = "The four entry files of the frontend, in the order they run";

/** A row: a file or folder name, then one short line (backticks mark code names). */
type Row = readonly [name: string, text: string];
/** A box: its folder (printed with a slash at the end), its title, its rows. */
type Box = readonly [path: string, title: string, rows: readonly Row[]];

export const ENTRY_TITLE = "The entry files";
export const ENTRY_FILES: readonly Row[] = [
  ["main.tsx", "It mounts the providers."],
  ["App.tsx", "It registers every page and its path."],
  ["components/protected-route.tsx", "`ProtectedRoute` and `SalesWorkRoute` guard the pages."],
  ["components/app-shell.tsx", "`AppShell` draws the rail, the top bar and the page."],
];

export const FEATURES_PATH = "src/features";
export const FEATURES_TITLE = "One folder for each page";
/** The feature folders. Each one owns the page at the path of its name, but for one. */
export const FEATURE_FOLDERS =
  "queue case-review dashboard training help docs rules feedback discussions roadmap learned addin".split(
    " ",
  );
const CASE_PAGE = "/case/:id";

export const SHARED_BOXES: readonly Box[] = [
  [
    "src/components",
    "Shared components",
    [
      ["figures/", "The drawings. `FIGURES` in `index.ts` maps a name to a component."],
      ["column-cards.tsx", "The rows of a table as cards, for a phone."],
    ],
  ],
  [
    "src/lib",
    "Maps of record",
    [
      ["sales-flow.ts", "The labels of record."],
      ["line-icons.ts", "The one icon map."],
      ["data.ts", "The data seam: from the wire shape to the UI model."],
      ["features.ts", "`FEATURES`, the UI switches in code."],
    ],
  ],
  [
    "src/lib/api",
    "The one door to the backend",
    [
      ["index.ts", "The barrel. A component imports from `@/lib/api` only."],
      ["hooks.ts", "The handwritten hooks."],
      ["generated/", "The client, generated from `openapi/openapi.json`."],
    ],
  ],
];

export const TESTS_BOX: Box = [
  "tests",
  "Tests that guard the layers",
  [
    ["*.test.mjs", "Node tests. They render the pages on the server through Vite."],
    ["*-render.tsx", "The render harnesses of the tests."],
  ],
];

export const STRUCTURE_NOTE =
  "Every path is under `frontend/`. The browser tests with Playwright are under `frontend/e2e/`, and `make test` does not run them.";

const BOX = "flex min-w-0 flex-col gap-2";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography key={index} as="code" variant="code" className="text-[0.9em] break-words">
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

/** The word on an arrow between two layers, with the arrow under it. */
function Down({ word }: { word: string }) {
  return (
    <span className="text-fg-muted flex flex-col items-center text-xs">
      {word}
      <Icon icon={ArrowDown} size="sm" label="to" />
    </span>
  );
}

/** A layer: a card with the folder (printed with a slash), the title, and the content. */
function Layer(props: { path: string; title: string; flask?: boolean; children: ReactNode }) {
  return (
    <Card variant="outline" padding="sm" accent="left" className={BOX}>
      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        <Icon icon={props.flask ? FlaskConical : Folder} size="sm" className="text-fg-muted" />
        <Typography as="code" variant="code" className="font-semibold break-all">
          {props.path}/
        </Typography>
        <Typography variant="label">{props.title}</Typography>
      </span>
      {props.children}
    </Card>
  );
}

function BoxCard({ box: [path, title, rows], split }: { box: Box; split?: boolean }) {
  return (
    <Layer path={path} title={title} flask={path === TESTS_BOX[0]}>
      <ul className={split ? "grid grid-cols-2 gap-x-4 gap-y-1" : "flex flex-col gap-1"}>
        {rows.map(([name, text]) => (
          <li key={name} className="flex min-w-0 flex-col text-sm">
            <Typography as="code" variant="code" className="text-[0.85em] break-all">
              {name}
            </Typography>
            <span className="text-fg-muted break-words">{code(text)}</span>
          </li>
        ))}
      </ul>
    </Layer>
  );
}

/** `layout` overrides the media query for the render tests. Omit it in the app. */
export function FrontendStructureFigure(props: { caption?: string; layout?: "wide" | "stacked" }) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = props.layout ?? (narrow ? "stacked" : "wide");
  const wide = form === "wide";
  const entry: TimelineItem[] = ENTRY_FILES.map(([name, text]) => ({
    id: name,
    title: (
      <Typography as="code" variant="code" className="font-semibold wrap-anywhere">
        {name}
      </Typography>
    ),
    description: <span className="break-words">{code(text)}</span>,
  }));
  return (
    <Figure name="frontend-structure" title={FRONTEND_STRUCTURE_TITLE} caption={props.caption}>
      <Layer path="src" title={ENTRY_TITLE}>
        {/* DS-GAP(timeline-horizontal-wrap): the horizontal Timeline does not wrap, so it is
            vertical below 768 px. DS-GAP(timeline-horizontal-last-item): the last item takes
            the width of its text and squeezes the others, so a utility makes it equal. */}
        <Timeline
          orientation={wide ? "horizontal" : "vertical"}
          connector="arrow"
          items={entry}
          aria-label={ENTRY_SUMMARY}
          className="[&>li:last-child]:flex-1"
        />
      </Layer>
      <Down word="shows" />
      <Layer path={FEATURES_PATH} title={FEATURES_TITLE}>
        <ul data-layout={form} aria-label={FEATURES_TITLE} className="flex flex-wrap gap-1.5">
          {FEATURE_FOLDERS.map((folder) => (
            <li key={folder}>
              <Badge tone="neutral" variant="outline" size="sm" shape="rounded">
                <span className="font-mono">{folder}</span>
                <span className="text-fg-muted font-mono">
                  {folder === "case-review" ? CASE_PAGE : `/${folder}`}
                </span>
              </Badge>
            </li>
          ))}
        </ul>
      </Layer>
      <Down word="imports" />
      <div className={wide ? "grid grid-cols-3 items-start gap-2" : BOX}>
        {SHARED_BOXES.map((box) => (
          <BoxCard key={box[0]} box={box} />
        ))}
      </div>
      <BoxCard box={TESTS_BOX} split={wide} />
      <Typography variant="caption" tone="muted" className="break-words">
        {code(STRUCTURE_NOTE)}
      </Typography>
    </Figure>
  );
}
